import { memoSanitize } from '@axa-asia/emma-safely-set-inner-html';
/**
 * @fileoverview Generic component of a Media Player that supports playing Video, Youtube and Audio
 * copied from Class NeurumHealthSubCategoryCardDetails developed by Felix Yau
 * TODO: add GA tracking
 */
import React, { Component } from 'react';
import _ from 'lodash';
import YouTube from 'react-youtube';
import './styles.scss';
import { t } from '../../../../locales';
import { isIos } from '../../../../utils';
import {
  gaClickableElementCallback,
  getGaPagePath,
} from '../../../../utils/generalGaEventAnalytics';
import AudioPlayer from '../../../../components/AudioPlayer';
import { select } from '../../../../stores';
import { getToken } from '../../../../selectors/authSelectors';
import { handleGaError } from '../../../../utils/generalGaEventAnalytics';
import {
  getValidToken,
  getYouTubeVideoIdFromUrl,
  getYouTubePlayerStates,
  isApp,
} from '../../../../utils';

const VIDEO_PROGRESS_INTERVAL = 250; // ms
const epsilon = 10; //ms

export interface ImgConfig {
  mobile: {
    src: string;
  };
  desktop: {
    src: string;
  };
}

export interface NeurumHealthSubDetails {
  uuid: string;
  title: string;
  audioUrl?: string;
  videoUrl?: string;
  audioDesc?: string;
  youtubeId?: string;
}

export interface MediaPlayerProps {
  width?: string;
  height?: string;
  details: NeurumHealthSubDetails;
  currentAudioplayerTimes: any;
  lang: string;
  onFetchAudioplayer: (timeObj: any) => void;
  onPlay?: () => void;
}

export interface MediaPlayerState {
  descLength: number;
  hasPlayedVideo: boolean;
  willCleanUpFullScreenState: boolean;
}

export class MediaPlayer extends Component<MediaPlayerProps, MediaPlayerState> {
  detailsDescRef: any;
  videoRef?: HTMLVideoElement;
  timeouts: NodeJS.Timeout[];

  constructor(props: MediaPlayerProps) {
    super(props);
    this.state = {
      descLength: 0,
      hasPlayedVideo: false,
      willCleanUpFullScreenState: false,
    };
    this.timeouts = [];
    this.setAudioplayerPauseTime = this.setAudioplayerPauseTime.bind(this);
    this.updateWordCounts = this.updateWordCounts.bind(this);
    this.postMessageToEmma = this.postMessageToEmma.bind(this);
    this.initYouTubeVideoOnProgressInterval =
      this.initYouTubeVideoOnProgressInterval.bind(this);
    this.getProgressInPercentage = this.getProgressInPercentage.bind(this);
    this.onHtmlVideoTimeUpdate = _.throttle(
      this.onHtmlVideoTimeUpdate.bind(this),
      VIDEO_PROGRESS_INTERVAL,
    );
    this.shouldUpdateVideoPaused = this.shouldUpdateVideoPaused.bind(this);
    this.shouldUpdateVideoProgress = this.shouldUpdateVideoProgress.bind(this);
  }

  componentDidMount() {
    this.updateWordCounts();
  }

  componentDidUpdate(prevProps: MediaPlayerProps) {
    // fix word count when details are fetched after component did mount
    if (this.props.details.uuid && !prevProps.details.uuid) {
      this.updateWordCounts();
    }
  }

  componentWillUnmount() {
    this.timeouts.forEach(clearInterval);
    if (this.state.willCleanUpFullScreenState) {
      this.postMessageToEmma('@EMMA_JS/LEAVE_FULL_SCREEN');
    }
  }

  updateWordCounts() {
    const description =
      this.detailsDescRef && this.detailsDescRef.innerText
        ? this.detailsDescRef.innerText
        : '';
    const desc = description.replace(/[\[\]\s\?\.!-;,:\'\"]+/g, ' ').trim();
    const chineseDesc = desc.match(/[\u4e00-\u9fa5]/g);
    const chineseDescLen = chineseDesc ? chineseDesc.length : 0;
    const EngDescLen = desc.trim() ? desc.trim().split(' ').length : 0;
    const descLen = chineseDesc ? chineseDescLen : EngDescLen;
    this.setState({ descLength: descLen });
  }

  // KPI #24 - Nenrum Audio play/pause button on click
  onClickToGatc(label: string) {
    const tcWindow: any = window;
    const token = select(getToken);
    const aToken = getValidToken(token);

    try {
      if (isApp()) {
        tcWindow.dataLayer.push({
          event: 'nenrumAudioClicks',
          nenrumAudioName: label,
          screenName: getGaPagePath(),
          AXAID: aToken,
        });
      } else {
        // HKEMMA-100917 - GA3 Decommission - Remove GA3
        // tcWindow.tC.event.nenrumAudioClicks(this, {
        //   nenrumAudioName: label,
        //   screenName: getGaPagePath(),
        // });
      }
    } catch (e) {
      handleGaError(e);
    }
  }

  onPlayVideoToGatc(label: string) {
    const tcWindow: any = window;
    const token = select(getToken);
    const aToken = getValidToken(token);
    const videoPlay = 'manual play';

    try {
      if (isApp()) {
        tcWindow.dataLayer.push({
          event: 'playVideo',
          videoPlay: videoPlay,
          videoName: label,
          screenName: getGaPagePath(),
          userId: aToken,
        });
      } else {
        // HKEMMA-100917 - GA3 Decommission - Remove GA3
        // tcWindow.tC.event.playVideo(this, {
        //   videoPlay: videoPlay,
        //   videoName: label,
        //   screenName: getGaPagePath(),
        //   AXAID: aToken,
        // });
      }
    } catch (e) {
      handleGaError(e);
    }
  }

  // KPI #56 - Video / YouTube video play state on change
  onChangeVideoStateToGatc(
    label: string,
    videoStatus: string,
    videoCurrentTime: number,
  ) {
    const tcWindow: any = window;
    const token = select(getToken);
    const aToken = getValidToken(token);

    try {
      if (isApp()) {
        tcWindow.dataLayer.push({
          event: 'watchVideo',
          videoStatus: videoStatus,
          videoName: label,
          videoCurrentTime: String(videoCurrentTime),
          screenName: getGaPagePath(),
          userId: aToken,
        });
      } else {
        // HKEMMA-100917 - GA3 Decommission - Remove GA3
        // tcWindow.tC.event.watchVideo(this, {
        //   videoStatus: videoStatus,
        //   videoName: label,
        //   videoCurrentTime: String(videoCurrentTime),
        //   screenName: getGaPagePath(),
        //   AXAID: aToken,
        // });
      }
    } catch (e) {
      handleGaError(e);
    }
  }

  setAudioplayerPauseTime(time: any) {
    const { details, currentAudioplayerTimes } = this.props;
    if (JSON.stringify(currentAudioplayerTimes) !== '{}') {
      const key = details.uuid;
      const updatedAudioplayerTimes = currentAudioplayerTimes;
      if (updatedAudioplayerTimes[key] || updatedAudioplayerTimes[key] === 0) {
        updatedAudioplayerTimes[key] = time;
      }
      this.props.onFetchAudioplayer(updatedAudioplayerTimes);
    }
  }

  getCurrentDeviceImg(imgObj: ImgConfig) {
    let checkIsApp = isApp();
    const offsetWidth = document.body.offsetWidth;
    if (offsetWidth < 769) {
      checkIsApp = true;
    } else {
      checkIsApp = false;
    }

    let imgSrc = '';
    if (imgObj) {
      const isShowMobileImg = checkIsApp && imgObj.mobile && imgObj.mobile.src;
      const isShowWebImg = !checkIsApp && imgObj.desktop && imgObj.desktop.src;
      if (isShowMobileImg) {
        imgSrc = imgObj.mobile.src;
      } else if (isShowWebImg) {
        imgSrc = imgObj.desktop.src;
      }
    }

    return imgSrc;
  }

  onVideoStateChange(title: string, newState: string, currentTime: number) {
    if (newState === 'Play' && !this.state.hasPlayedVideo) {
      this.onPlayVideoToGatc(title);
      this.setState({
        hasPlayedVideo: true,
      });
      this.props.onPlay && this.props.onPlay();
    }

    // mimic generic button click for video play/pause button
    if (newState === 'Play' || newState === 'Paused') {
      gaClickableElementCallback(
        'button',
        newState.toLowerCase(),
        getGaPagePath(),
      );
    }

    // console.log('[video] video state change', newState, currentTime);
    this.onChangeVideoStateToGatc(title, newState, Math.floor(currentTime));
  }

  initYouTubeVideoOnProgressInterval(
    target: any,
    onProgress: (currentTime: number) => void,
  ) {
    let prevTime = target.getCurrentTime();
    const updateYouTubeVideoProgress = () => {
      const currentTime = target.getCurrentTime();
      if (prevTime !== currentTime) {
        onProgress(currentTime);
        prevTime = currentTime;
      }
    };

    const id = setInterval(updateYouTubeVideoProgress, VIDEO_PROGRESS_INTERVAL);
    this.timeouts.push(id);
  }

  postMessageToEmma(action: string, payload?: any) {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          action,
          payload,
        }),
      );
    }
  }

  getProgressInPercentage(currentTime: number, duration: number) {
    const percentage = (currentTime / duration) * 100;
    return Math.round(percentage / 25) * 25; // round to 0, 25, 50, 75, 100
  }

  shouldUpdateVideoProgress(
    currentTime: number,
    duration: number,
    progressInPercentage: number,
  ) {
    const progress =
      progressInPercentage ||
      this.getProgressInPercentage(currentTime, duration);
    const diffInSecond = currentTime - (progress / 100) * duration;

    return (
      progress !== 0 &&
      progress !== 100 && // handled in onEnd
      diffInSecond < 0 && // fix duplicated event
      Math.abs(diffInSecond) < VIDEO_PROGRESS_INTERVAL / 1000
    );
  }

  shouldUpdateVideoPaused(currentTime: number, duration: number) {
    return Math.abs(currentTime - duration) > epsilon / 1000;
  }

  onHtmlVideoTimeUpdate(title: string) {
    const { currentTime, duration } = this.videoRef!;
    const progress = this.getProgressInPercentage(currentTime, duration);
    if (this.shouldUpdateVideoProgress(currentTime, duration, progress)) {
      this.onVideoStateChange(title, `${progress}% Watched`, currentTime);
    }
  }

  render() {
    const { uuid, title, audioUrl, videoUrl, youtubeId, audioDesc } =
      this.props.details;
    const { currentAudioplayerTimes, lang, height, width } = this.props;
    const { descLength } = this.state;
    const checkIsApp = isApp();
    const initTime =
      (!checkIsApp &&
        currentAudioplayerTimes &&
        JSON.stringify(currentAudioplayerTimes)) !== '{}'
        ? currentAudioplayerTimes[uuid]
        : 0;

    const youtubeVideoId = youtubeId
      ? youtubeId
      : videoUrl
      ? getYouTubeVideoIdFromUrl(videoUrl)
      : null;
    const playerStates = getYouTubePlayerStates();

    return (
      <div className="media-player-card-details">
        <div
          className={`media-player-card-details__content ${
            videoUrl ? 'media-player-card-details__with-video' : ''
          } ${
            audioUrl && !audioDesc
              ? 'media-player-card-details__only-audio'
              : ''
          }`}
        >
          {audioUrl && (
            <div
              className={`${
                audioDesc ? 'media-player-card-details__audio-desc' : ''
              }`}
            >
              <AudioPlayer
                src={audioUrl}
                title={title}
                initialCurrentTime={initTime}
                onProgress={this.setAudioplayerPauseTime}
                trackId={uuid}
                onPlayBackButtonClick={() => {
                  this.onClickToGatc(title);
                }}
              />
            </div>
          )}
          {!!(videoUrl || youtubeVideoId) && (
            <div
              className={`${
                audioDesc ? 'media-player-card-details__video-desc' : ''
              }`}
            >
              {youtubeVideoId ? (
                <YouTube
                  id="youtube"
                  videoId={youtubeVideoId}
                  containerClassName="youtube-player"
                  opts={{
                    playerVars: {
                      hl: lang === 'zh' ? 'zh-hk' : 'en',
                      modestbranding: 1,
                      fs: 1,
                    },
                    width: width,
                    height: height,
                  }}
                  onStateChange={(event) => {
                    const { playerState, currentTime, duration } =
                      event.target.playerInfo;
                    const { videoTitle = '' } = event.target;
                    switch (playerState) {
                      case playerStates.playing:
                        this.onVideoStateChange(
                          videoTitle || title,
                          'Play',
                          currentTime,
                        );
                        break;
                      case playerStates.paused:
                        if (
                          this.shouldUpdateVideoPaused(currentTime, duration)
                        ) {
                          this.onVideoStateChange(
                            videoTitle || title,
                            'Pause',
                            currentTime,
                          );
                        }
                        break;
                      case playerStates.buffering:
                        this.onVideoStateChange(
                          videoTitle || title,
                          'Buffering',
                          currentTime,
                        );
                        break;
                      case playerStates.ended:
                        this.onVideoStateChange(
                          videoTitle || title,
                          '100% Watched',
                          currentTime,
                        );
                    }

                    // workaround for ios not able to detect fullscreen event of iframe video
                    // when video is playing, user session should never timeout
                    if (isApp() && isIos()) {
                      if (playerState === playerStates.playing) {
                        this.postMessageToEmma('@EMMA_JS/ENTER_FULL_SCREEN');
                        this.setState({ willCleanUpFullScreenState: true });
                      } else {
                        this.postMessageToEmma('@EMMA_JS/EXIT_FULL_SCREEN');
                        this.setState({ willCleanUpFullScreenState: false });
                      }
                    }
                  }}
                  onReady={(event) => {
                    this.initYouTubeVideoOnProgressInterval(
                      event.target,
                      (currentTime) => {
                        const { duration } = event.target.playerInfo;
                        const { videoTitle = '' } = event.target;
                        const progress = this.getProgressInPercentage(
                          currentTime,
                          duration,
                        );
                        if (
                          this.shouldUpdateVideoProgress(
                            currentTime,
                            duration,
                            progress,
                          )
                        ) {
                          this.onVideoStateChange(
                            videoTitle || title,
                            `${progress}% Watched`,
                            currentTime,
                          );
                        }
                      },
                    );
                  }}
                />
              ) : (
                <video
                  width={width}
                  height={height}
                  controls
                  preload="metadata"
                  ref={(ref: any) => {
                    this.videoRef = ref;
                  }}
                  onPlay={() => {
                    const { currentTime } = this.videoRef!;
                    this.onVideoStateChange(title, 'Play', currentTime);
                  }}
                  onPause={() => {
                    const { currentTime, duration } = this.videoRef!;
                    if (this.shouldUpdateVideoPaused(currentTime, duration)) {
                      this.onVideoStateChange(title, 'Pause', currentTime);
                    }
                  }}
                  onEnded={() => {
                    this.onVideoStateChange(
                      title,
                      '100% Watched',
                      this.videoRef!.currentTime,
                    );
                  }}
                  onTimeUpdate={() => this.onHtmlVideoTimeUpdate(title)}
                  onWaiting={() =>
                    this.onVideoStateChange(
                      title,
                      'Buffering',
                      this.videoRef!.currentTime,
                    )
                  }
                >
                  <source src={`${videoUrl}#t=0.1`} type="video/mp4" />
                </video>
              )}
            </div>
          )}
          {audioDesc && (
            <div
              className={`${
                audioUrl
                  ? 'media-player-card-details__desc-audio'
                  : 'media-player-card-details__desc-no-audio'
              }`}
            >
              <div className="media-player-card-details__content-desc-length">
                {descLength} {t('WORDS')}
              </div>
              <div
                className="media-player-card-details__content-desc"
                ref={(ref: any) => {
                  this.detailsDescRef = ref;
                }}
                dangerouslySetInnerHTML={{ __html: memoSanitize(audioDesc) }}
              ></div>
            </div>
          )}
        </div>
      </div>
    );
  }

  static mapStateToProps(state: any, ownProps: any) {
    return {};
  }

  static mapDispatchToProps(dispatch: any) {
    return {};
  }
}
